import React from 'react';
import { TrendingUp, TrendingDown, Users, DollarSign, Target, Activity, ArrowUpRight } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { c } from '@/config/region';

interface DashboardContentProps {
  animationDelay?: number;
}

const stats = [
  { label: 'Leads', value: '2,847', change: '+12.5%', trend: 'up', icon: Users, color: '#3B82F6' },
  { label: 'Revenue', value: c('$48.2K'), change: '+8.2%', trend: 'up', icon: DollarSign, color: '#10B981' },
  { label: 'Conv.', value: '24.8%', change: '-2.1%', trend: 'down', icon: Target, color: '#F59E0B' },
  { label: 'Deals', value: '156', change: '+5.7%', trend: 'up', icon: Activity, color: '#8B5CF6' },
];

const leads = [
  { initials: 'SJ', name: 'Sarah J.', email: 's.johnson', value: c('$12.5K'), status: 'Hot' },
  { initials: 'MC', name: 'Mike C.', email: 'm.chen', value: c('$8.2K'), status: 'Warm' },
  { initials: 'ED', name: 'Emily D.', email: 'emily.d', value: c('$24K'), status: 'Hot' },
];

const DashboardContent: React.FC<DashboardContentProps> = ({ animationDelay = 0 }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div 
      className="flex-1 p-1.5 sm:p-2 lg:p-4 overflow-hidden"
      style={{ 
        backgroundColor: isDark ? '#222222' : '#F9F9F9',
        animation: `fadeIn 0.4s ease-out ${animationDelay}ms both`
      }}
    >
      {/* Stats Row */}
      <div 
        className="grid grid-cols-4 gap-1 sm:gap-1.5 lg:gap-3 mb-1.5 sm:mb-2 lg:mb-4"
        style={{ animation: `slideInFromTop 0.3s ease-out ${animationDelay + 100}ms both` }}
      >
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          const TrendIcon = stat.trend === 'up' ? TrendingUp : TrendingDown;
          return (
            <div
              key={index}
              className="p-1 sm:p-1.5 lg:p-3 rounded-lg"
              style={{ 
                backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
                border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
                animation: `scaleIn 0.3s ease-out ${animationDelay + 150 + (index * 60)}ms both`
              }}
            >
              <div className="flex items-center justify-between mb-0.5 lg:mb-1">
                <Icon className="w-2 h-2 sm:w-2.5 sm:h-2.5 lg:w-4 lg:h-4" style={{ color: stat.color }} />
                <div 
                  className="hidden sm:flex items-center gap-0.5 text-[5px] lg:text-[10px] font-medium"
                  style={{ color: stat.trend === 'up' ? '#22C55E' : '#EF4444' }}
                >
                  <TrendIcon className="w-1.5 h-1.5 lg:w-3 lg:h-3" />
                  <span className="hidden lg:inline">{stat.change}</span>
                </div>
              </div>
              <div 
                className="text-[8px] sm:text-[10px] lg:text-lg font-bold"
                style={{ color: isDark ? '#FFFFFF' : '#111111' }}
              >
                {stat.value}
              </div>
              <div 
                className="text-[5px] sm:text-[6px] lg:text-[10px] mt-0.5"
                style={{ color: isDark ? '#888888' : '#666666' }}
              >
                {stat.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Content Grid */}
      <div 
        className="grid grid-cols-2 gap-1.5 sm:gap-2 lg:gap-4"
        style={{ animation: `fadeIn 0.4s ease-out ${animationDelay + 400}ms both` }}
      >
        {/* Chart Section */}
        <div 
          className="rounded-lg overflow-hidden"
          style={{ 
            backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
            border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`
          }}
        >
          <div 
            className="px-1.5 sm:px-2 lg:px-3 py-1 lg:py-2 border-b flex items-center justify-between"
            style={{ borderColor: isDark ? '#333333' : '#E5E5E5' }}
          >
            <span 
              className="text-[7px] sm:text-[8px] lg:text-xs font-semibold"
              style={{ color: isDark ? '#FFFFFF' : '#111111' }}
            >
              Revenue
            </span>
            <span 
              className="text-[5px] sm:text-[6px] lg:text-[10px] hidden sm:block"
              style={{ color: isDark ? '#888888' : '#666666' }}
            >
              Monthly
            </span>
          </div>
          <div className="p-1.5 sm:p-2 lg:p-3">
            {/* Mini Chart Bars */}
            <div className="flex items-end gap-0.5 sm:gap-1 h-10 sm:h-12 lg:h-20">
              {[40, 65, 45, 80, 55, 70, 90, 60, 85, 75, 95, 88].map((height, i) => (
                <div 
                  key={i}
                  className="flex-1 rounded-sm"
                  style={{ 
                    height: `${height}%`,
                    backgroundColor: i === 11 ? '#3B82F6' : (isDark ? '#333333' : '#E5E5E5'),
                    animation: `scaleIn 0.3s ease-out ${animationDelay + 500 + (i * 30)}ms both`,
                    transformOrigin: 'bottom'
                  }}
                />
              ))}
            </div>
            {/* Chart Legend - hidden on mobile */}
            <div className="hidden sm:flex justify-between mt-1 lg:mt-2">
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full" style={{ backgroundColor: '#3B82F6' }} />
                <span className="text-[5px] lg:text-[8px]" style={{ color: isDark ? '#888888' : '#666666' }}>Revenue</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full" style={{ backgroundColor: isDark ? '#333333' : '#E5E5E5' }} />
                <span className="text-[5px] lg:text-[8px]" style={{ color: isDark ? '#888888' : '#666666' }}>Target</span>
              </div>
            </div>
          </div>
        </div>

        {/* Leads Section */}
        <div 
          className="rounded-lg"
          style={{ 
            backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
            border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`
          }}
        >
          <div 
            className="px-1.5 sm:px-2 lg:px-3 py-1 lg:py-2 border-b flex items-center justify-between"
            style={{ borderColor: isDark ? '#333333' : '#E5E5E5' }}
          >
            <span 
              className="text-[7px] sm:text-[8px] lg:text-xs font-semibold"
              style={{ color: isDark ? '#FFFFFF' : '#111111' }}
            >
              Leads
            </span>
            <span 
              className="text-[5px] sm:text-[6px] lg:text-[10px] hidden sm:flex items-center gap-0.5"
              style={{ color: '#1D4ED8' }}
            >
              All <ArrowUpRight className="w-2 h-2 lg:w-3 lg:h-3" />
            </span>
          </div>
          <div className="p-1 sm:p-1.5 lg:p-2 space-y-1 lg:space-y-2">
            {leads.map((lead, index) => (
              <div
                key={index}
                className="flex items-center gap-1 sm:gap-1.5 lg:gap-2 p-1 lg:p-1.5 rounded"
                style={{ 
                  backgroundColor: isDark ? '#252525' : '#FAFAFA',
                  animation: `slideInFromRight 0.3s ease-out ${animationDelay + 600 + (index * 80)}ms both`
                }}
              >
                <div 
                  className="w-4 h-4 sm:w-5 sm:h-5 lg:w-8 lg:h-8 rounded-full flex items-center justify-center text-[5px] sm:text-[6px] lg:text-[10px] font-medium"
                  style={{ 
                    backgroundColor: isDark ? '#333333' : '#E5E5E5',
                    color: isDark ? '#FFFFFF' : '#111111'
                  }}
                >
                  {lead.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div 
                    className="text-[6px] sm:text-[7px] lg:text-[11px] font-medium"
                    style={{ color: isDark ? '#FFFFFF' : '#111111' }}
                  >
                    {lead.name}
                  </div>
                  <div 
                    className="text-[5px] sm:text-[6px] lg:text-[9px] hidden sm:block"
                    style={{ color: isDark ? '#888888' : '#666666' }}
                  >
                    {lead.email}
                  </div>
                </div>
                <div className="text-right">
                  <div 
                    className="text-[6px] sm:text-[7px] lg:text-[11px] font-medium"
                    style={{ color: isDark ? '#FFFFFF' : '#111111' }}
                  >
                    {lead.value}
                  </div>
                  <span 
                    className="text-[5px] sm:text-[6px] lg:text-[8px] px-0.5 lg:px-1 py-0.5 rounded hidden sm:inline-block"
                    style={{ 
                      backgroundColor: lead.status === 'Hot' ? '#FEE2E2' : '#FEF3C7',
                      color: lead.status === 'Hot' ? '#DC2626' : '#D97706'
                    }}
                  >
                    {lead.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardContent;
